import type { TeamMemberSummary } from "./team-summary";

/**
 * What the company view's filter controls hold. Every list is a set of ids the
 * viewer picked; **empty means "no filter on this facet"**, which is the
 * convention `FilterToggleGroup` and the entity comboboxes already use and the
 * reason nothing here needs a separate "enabled" flag.
 */
export interface TeamFilters {
  pillarIds: string[];
  departmentIds: string[];
  roleTeamIds: string[];
  roleIds: string[];
  employmentTypes: string[];
}

export const EMPTY_TEAM_FILTERS: TeamFilters = {
  pillarIds: [],
  departmentIds: [],
  roleTeamIds: [],
  roleIds: [],
  employmentTypes: [],
};

/** How many individual values are selected — the count on the Filters header. */
export function activeTeamFilterCount(filters: TeamFilters): number {
  return (
    filters.pillarIds.length +
    filters.departmentIds.length +
    filters.roleTeamIds.length +
    filters.roleIds.length +
    filters.employmentTypes.length
  );
}

export function hasTeamFilters(filters: TeamFilters, search: string): boolean {
  return activeTeamFilterCount(filters) > 0 || search.trim().length > 0;
}

/** A single-valued facet: no selection passes everything, null never matches one. */
function matchesOne(selected: string[], value: string | null): boolean {
  if (selected.length === 0) return true;
  return value !== null && selected.includes(value);
}

/**
 * A many-valued facet. Matches when the person holds ANY of the selected
 * values — someone who is both an Engineer and a Project Manager belongs in a
 * filter for either.
 */
function matchesAny(selected: string[], values: string[]): boolean {
  if (selected.length === 0) return true;
  return values.some((v) => selected.includes(v));
}

/**
 * Whether a person is on screen under the current filters.
 *
 * **OR within a facet, AND across facets** — picking two departments widens,
 * picking a department and an employment type narrows. That is what every other
 * filtered table in the app does, and the one rule people expect without being
 * told.
 *
 * Search matches name or job TITLE, not the roles. The title is what the table
 * shows beside the name, so searching for "engineer" ought to find the rows
 * that say Engineer; the roles someone holds are the Job role filter's job.
 *
 * A pure function on the already-loaded payload: the company roster arrives in
 * one response, the gateway calls behind it are expensive, and re-fetching per
 * filter combination would pay for them again for a result the client already
 * has.
 */
export function matchesTeamFilters(
  member: TeamMemberSummary,
  filters: TeamFilters,
  search: string,
): boolean {
  const needle = search.trim().toLowerCase();
  if (needle.length > 0) {
    const haystack = `${member.name} ${member.title ?? ""}`.toLowerCase();
    if (!haystack.includes(needle)) return false;
  }
  return (
    matchesOne(filters.pillarIds, member.pillarId) &&
    matchesOne(filters.departmentIds, member.departmentId) &&
    matchesOne(filters.roleTeamIds, member.roleTeamId) &&
    matchesOne(filters.employmentTypes, member.employmentType) &&
    matchesAny(filters.roleIds, member.roleIds)
  );
}
